import React, { useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { StarRating } from '@/components/VehicleTrust';
import { ShieldCheck, AlertTriangle, X, Loader2, Send } from 'lucide-react';

export const POSITIVE_TAGS = ['felt_safe', 'courteous', 'safe_driving', 'clean_vehicle', 'fair_fare', 'on_time'];
export const DANGER_TAGS = ['reckless', 'uncomfortable', 'overcharged', 'refused_meter', 'intoxicated', 'verbal_harassment', 'unwanted_contact', 'suspicious_detour'];

const Chip = ({ label, active, danger, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    aria-pressed={active}
    className="rounded-full px-3 py-2 text-xs font-semibold border transition-colors"
    style={{
      borderColor: active ? (danger ? 'var(--danger)' : 'var(--primary)') : 'var(--border)',
      color: active ? (danger ? 'var(--danger)' : 'var(--primary)') : 'var(--text-muted)',
      background: active
        ? `color-mix(in srgb, ${danger ? 'var(--danger)' : 'var(--primary)'} 12%, transparent)`
        : 'var(--surface)',
    }}
  >
    {label}
  </button>
);

export const RideRating = ({ open, ride, onClose, onSubmitted }) => {
  const t = useT();
  const [rating, setRating] = useState(0);
  const [positive, setPositive] = useState([]);
  const [danger, setDanger] = useState([]);
  const [note, setNote] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [plate, setPlate] = useState(ride?.plate || '');
  const [busy, setBusy] = useState(false);

  if (!open || !ride) return null;

  const toggle = (list, setList, key) =>
    setList(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);

  const hasInput = rating > 0 || positive.length || danger.length || note.trim();

  const submit = async () => {
    if (!hasInput) { onClose && onClose(); return; }
    setBusy(true);
    try {
      const res = await api.submitFeedback(ride.ride_id, {
        rating: rating > 0 ? rating : null,
        positive_tags: positive,
        danger_tags: danger,
        note: note.trim() || null,
        anonymous,
        plate: plate.trim() || null,
      });
      api.track('ride_rated', { danger: danger.length > 0 });
      toast.success(danger.length ? t('rr_thanks_danger') : t('rr_thanks'));
      onSubmitted && onSubmitted(res);
      onClose && onClose();
    } catch {
      toast.error(t('rr_err'));
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)' }}
      data-testid="ride-rating-modal"
    >
      <div
        className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border overflow-y-auto"
        style={{ background: 'var(--surface)', borderColor: 'var(--border)', maxHeight: '92vh', boxShadow: 'var(--shadow)' }}
      >
        <div className="p-5">
          <div className="flex items-start justify-between mb-1">
            <div>
              <h2 className="text-xl font-extrabold" style={{ color: 'var(--text)' }}>{t('rr_title')}</h2>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{t('rr_sub')}</p>
            </div>
            <button onClick={onClose} aria-label={t('rr_skip')} data-testid="ride-rating-close" className="w-9 h-9 rounded-full flex items-center justify-center" style={{ color: 'var(--text-muted)' }}><X size={20} /></button>
          </div>

          {/* Stars */}
          <div className="flex justify-center my-3">
            <StarRating value={rating} onChange={setRating} size={34} testId="ride-rating-stars" />
          </div>

          {/* Positive */}
          <div className="flex items-center gap-1.5 mt-4 mb-2">
            <ShieldCheck size={16} style={{ color: 'var(--primary)' }} />
            <span className="text-sm font-bold" style={{ color: 'var(--text)' }}>{t('rr_positive')}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {POSITIVE_TAGS.map((k) => (
              <Chip key={k} label={t(`tag_${k}`)} active={positive.includes(k)} onClick={() => toggle(positive, setPositive, k)} testid={`rr-pos-${k}`} />
            ))}
          </div>

          {/* Danger */}
          <div className="flex items-center gap-1.5 mt-5 mb-2">
            <AlertTriangle size={16} style={{ color: 'var(--danger)' }} />
            <span className="text-sm font-bold" style={{ color: 'var(--danger)' }}>{t('rr_danger')}</span>
          </div>
          <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>{t('rr_danger_hint')}</p>
          <div className="flex flex-wrap gap-2">
            {DANGER_TAGS.map((k) => (
              <Chip key={k} label={t(`tag_${k}`)} active={danger.includes(k)} danger onClick={() => toggle(danger, setDanger, k)} testid={`rr-dng-${k}`} />
            ))}
          </div>

          {/* Plate (only if the ride has none) */}
          {!ride?.plate && (
            <div className="mt-5">
              <label className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{t('rr_plate_label')}</label>
              <input
                value={plate}
                onChange={(e) => setPlate(e.target.value)}
                placeholder={t('rr_plate_ph')}
                data-testid="ride-rating-plate"
                className="w-full mt-1 rounded-xl px-3 min-h-[46px] border text-sm uppercase outline-none"
                style={{ background: 'var(--surface-alt)', borderColor: 'var(--border)', color: 'var(--text)' }}
              />
            </div>
          )}

          {/* Note */}
          <div className="mt-4">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('rr_note_ph')}
              rows={2}
              data-testid="ride-rating-note"
              className="w-full rounded-xl px-3 py-2 border text-sm outline-none resize-none"
              style={{ background: 'var(--surface-alt)', borderColor: 'var(--border)', color: 'var(--text)' }}
            />
          </div>

          {/* Anonymous */}
          <label className="mt-3 flex items-center gap-2 cursor-pointer select-none" data-testid="ride-rating-anon">
            <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="w-4 h-4 accent-current" style={{ accentColor: 'var(--primary)' }} />
            <span className="text-sm" style={{ color: 'var(--text)' }}>{t('rr_anon')}</span>
          </label>

          <div className="mt-5 flex gap-2">
            <button onClick={onClose} data-testid="ride-rating-skip" className="flex-1 inline-flex items-center justify-center rounded-full font-semibold min-h-[50px] border" style={{ borderColor: 'var(--border)', color: 'var(--text-muted)', background: 'var(--surface)' }}>{t('rr_skip')}</button>
            <button onClick={submit} disabled={busy} data-testid="ride-rating-submit" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full font-bold min-h-[50px] disabled:opacity-60" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
              {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} {t('rr_submit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
